import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Checkbox, Container, FormControlLabel, Grid, makeStyles, MenuItem, Select, Typography } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import moment from 'moment';
import { Link } from 'react-router-dom';

import ImageUploadFields from './ImageUploadFields';
import ImageUploadOptionalFields from './ImageUploadOptionalFields';
import NotificationSettings from './NotificationSettings';
import OnlinePortalFields from './OnlinePortalFields';
import PaymentMethod from './PaymentMethod';
import TimingOptions from './TimingOptions';
import { adminCaseUploadOptions, allCaseUploadOptions, lawFirmCaseUploadOptions } from '../../models/Case';
import CreateCaseModel from '../../models/CreateCaseModel';
import { WorkspaceProductType } from '../../models/Workspace';
import { debounce } from '../../utils/lodashUtils';
import EditFields from '../common/EditFields';
import LoadingButton from '../common/LoadingButton';
import TooltipIcon from '../common/TooltipIcon';
import FolderLinkForm from '../personalizedVideo/FolderLinkForm';

const validations = {
  complete: CreateCaseModel.validateComplete,
  create: CreateCaseModel.validateCreate,
  lawFirm: CreateCaseModel.validateLawFirm,
};

const useStyles = makeStyles((theme) => {
  return {
    checkboxContainer: {
      marginTop: '16px',
    },
    label: {
      padding: '1rem 0',
    },
    link: {
      color: theme.palette?.primary?.main,
      textDecoration: 'none',
    },
    poBoxAddress: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      justifyContent: 'center',
    },
    statsContainer: {
      background: '#F5F5F5',
      borderRadius: '10px',
      fontSize: '11px',
      padding: '7px 14px',
      textAlign: 'left',
    },
    statsText: {
      fontSize: '11px',
      fontWeight: 'bold',
    },
    typographyLabel: {
      color: '#999999',
      fontSize: '11px',
      lineHeight: '16px',
      transform: 'translateY(2px)',
    },
    uploadActions: {
      color: '#8C8C8C',
      display: 'flex',
      fontSize: '12px',
      fontWeight: '400',
      marginTop: '1rem',
      width: 'fit-content',
    },
  };
});

const revelantInfoDefaultText = `Brief description of injury:

Date of Injury (DOI):

Claim number:

Allowed Conditions:

Alleged Conditions:

Attorney name:`;

const preliminaryInfo = 'We will try to get a preliminary review to you by this date. Ensure that there will be time for any possible discussion and revisions prior to a hearing.';

export default ({
  onEmailChange,
  patientData,
  filesStatus,
  onFilesUpload,
  onReportUpload,
  onRemoveFiles,
  onRemoveReport,
  onCancelClick,
  onSubmit,
  onInvalidFiles,
  validationErrors,
  mode,
  onOpenUploadReportDialog,
  isSubmitting$,
  additionalFiles,
  onOpenAdditionalFilesDialog,
  onRemoveAdditionalFiles,
  onImageUploadOptionChange,
  onChangeFormOnlinePortalAccess,
  onChangeOnlineFolderLink,
  termsCheck,
  onUpdateTermsCheck,
  admins,
  onUpdateSelectedAdmins,
  notifyMe,
  onUpdateNotifyMe,
  selectedAdmins,
  userDetails,
  selectedPaymentMethod,
  onUpdatePaymentMethod,
  selectedPackage,
  onUpdateSelectedPackage,
  discountData,
  workspaceDetail,
  onOpenSelectImageUploadDialog,
  onButtonEnabled,
}) => {
  const classes = useStyles();
  const [patient, setPatient] = useState({});
  const [patientId, setPatientId] = useState(null);
  const [status, setStatus] = useState({});
  const [errors, setErrors] = useState({});
  const firstGroupRef = useRef();
  const secondGroupRef = useRef();
  const thirdGroupRef = useRef();
  const fourthGroupRef = useRef();
  const debouncedEmailChangeRef = useRef(null);
  const [uploadedReport, setUploadedReport] = useState(null);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [areRequiredFieldsFilled, setAreRequiredFieldsFilled] = useState(false);

  const argumentInfo = (
    <>
      If the imaging were to support your position, what is the specific argument you are trying to make?
      <br />
      <br /> Some possible examples:
      <ul>
        <li>There is no evidence of the claimed injury (specify).</li>
        <li>The findings are chronic and not due to acute injury.</li>
        <li>There is no substantial aggravation of the known existing condition (specify condition).</li>
      </ul>
    </>
  );

  const handleRemoveAdditionalFiles = (index) => {
    if (onRemoveAdditionalFiles) onRemoveAdditionalFiles(index);
  };

  const handleOpenAdditionalFilesDialog = () => {
    if (onOpenAdditionalFilesDialog) onOpenAdditionalFilesDialog();
  };

  const handleRadioChange = (event) => {
    if (onImageUploadOptionChange) onImageUploadOptionChange(event.target.value);
  };

  const handleChangeFormOnlinePortalAccess = (value, field, validForm) => {
    if (onChangeFormOnlinePortalAccess) onChangeFormOnlinePortalAccess(value, field, validForm);
  };

  const handleTermsCheckChange = (target, e) => {
    if (onUpdateTermsCheck) onUpdateTermsCheck(target, e.target.checked);
  };

  const handleUpdateSelectedAdmins = (data) => {
    if (onUpdateSelectedAdmins) onUpdateSelectedAdmins(data || []);
  };

  const handleUpdateNotifyMe = (data) => {
    if (onUpdateNotifyMe) onUpdateNotifyMe(data);
  };

  const handleUpdatePaymentMethod = (data) => {
    if (onUpdatePaymentMethod) onUpdatePaymentMethod(data);
  };

  const handleUpdateSelectedPackage = (data) => {
    if (onUpdateSelectedPackage) onUpdateSelectedPackage(data);
  };

  useEffect(() => {
    if (uploadedReport) {
      onReportUpload(uploadedReport);
    }
  }, [uploadedReport, onReportUpload]);

  useEffect(() => {
    setPatient(patientData);
    if (patientData?.id) {
      setPatientId(patientData?.id);
    }
  }, [patientData]);

  useEffect(() => {
    setErrors(validationErrors);
  }, [validationErrors]);

  useEffect(() => {
    setStatus(filesStatus);
  }, [filesStatus]);

  const isLawFirm = useMemo(() => [userDetails?.workspace?.productType].includes(WorkspaceProductType.LawFirm.value), [userDetails]);

  const checkRequiredFields = useCallback(() => {
    const firstData = firstGroupRef.current?.getFormData ? firstGroupRef.current.getFormData() : {};
    const secondData = secondGroupRef.current?.getFormData ? secondGroupRef.current.getFormData() : {};
    const fourthData = fourthGroupRef.current?.getFormData ? fourthGroupRef.current.getFormData() : {};

    let requiredFields = [];
    let formData = {};

    if (isLawFirm) {
      requiredFields = ['name', 'dateOfBirth', 'gender', 'employer', 'preliminaryDate', 'argument'];
      formData = { ...firstData, ...fourthData };
    } else {
      requiredFields = ['email', 'dateOfBirth', 'preferredName', 'name', 'phoneNumber', 'gender'];
      formData = { ...firstData, ...secondData };
    }

    const allFilled = requiredFields.every((field) => {
      const value = formData[field];
      return value !== undefined && value !== null && value !== '';
    });

    setAreRequiredFieldsFilled(allFilled);
  }, [isLawFirm]);

  useEffect(() => {
    const interval = setInterval(() => {
      checkRequiredFields();
    }, 500);

    return () => clearInterval(interval);
  }, [checkRequiredFields]);

  const onSubmitClick = useCallback(async () => {
    const firstData = await firstGroupRef.current.getValidData();
    const secondData = secondGroupRef.current?.getValidData ? await secondGroupRef.current.getValidData() : {};
    const thirdData = await thirdGroupRef.current.getValidData();
    const fourthData = fourthGroupRef.current?.getValidData ? await fourthGroupRef.current.getValidData() : {};

    const statusFileType = status?.fileType || 'dcm';

    let isValid = status?.uploaded && status?.imageCount > 0;

    if (statusFileType !== 'dcm') isValid = status.uploaded;

    if (workspaceDetail?.radiologyReportRequired) {
      isValid = status.uploaded && status.imageCount > 0 && (status?.uploadedReport?.length || status?.hasReport);
      if (statusFileType !== 'dcm') isValid = status.uploaded && status.uploadedReport?.length > 0;
    }

    if (status.imageUploadOption === allCaseUploadOptions.onlinePortal.value) {
      isValid =
        status.formOnlinePortalAccess.portalAddress &&
        status.formOnlinePortalAccess.portalUsername &&
        status.formOnlinePortalAccess.portalPassword &&
        status.formOnlinePortalAccess.studyName;
    }

    if (status.imageUploadOption === allCaseUploadOptions.shareFolderLink.value) {
      isValid = status.formOnlineFolderLink.onlineServiceName && status.formOnlineFolderLink.sharingLink && status.formOnlineFolderLink.studyDescription;
    }

    if (status.imageUploadOption === allCaseUploadOptions.mailToPOBox.value) {
      isValid = true;
    }

    if (firstData && secondData && thirdData && fourthData && isValid) {
      onSubmit({
        ...firstData,
        ...secondData,
        ...thirdData,
        ...fourthData,
        isLawFirm,
        userId: patientId,
        workspaceId: patient?.workspaceId || workspaceDetail?.id,
      });
    } else {
      setShowAllErrors(true);
      onInvalidFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, patient, workspaceDetail, workspaceDetail?.radiologyReportRequired]);

  const debouncedEmailChange = useCallback(
    (value) => {
      if (debouncedEmailChangeRef.current) debouncedEmailChangeRef.current.cancel();

      debouncedEmailChangeRef.current = debounce(() => {
        onEmailChange(value);
        setPatientId(null);
      }, 500);

      debouncedEmailChangeRef.current();
    },
    [onEmailChange]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleEmailChange = (value) => debouncedEmailChange(value);

  const validationMode = useMemo(() => (isLawFirm ? 'lawFirm' : mode), [isLawFirm, mode]);

  const fieldGroup1 = useMemo(() => {
    return isLawFirm
      ? [
          { label: "Claimant's name", name: 'name', placeholder: 'Name', required: true, type: 'text' },
          { label: "Claimant's Date of Birth", name: 'dateOfBirth', onlyDate: true, placeholder: '11/23/2020', required: true, type: 'date' },
          {
            items: [
              { label: 'Female', value: 'female' },
              { label: 'Male', value: 'male' },
              { label: 'Other', value: 'other' },
            ],
            label: "Claimant's Gender",
            name: 'gender',
            placeholder: 'Select Gender',
            required: true,
            type: 'select',
          },
          { autoComplete: 'on', label: "Claimant's Employer", name: 'employer', placeholder: 'Employer', required: true, type: 'text' },
          {
            customLabel: (
              <span style={{ color: '#172b4d', display: 'flex', gap: '8px' }}>
                Requested date of preliminary report<span style={{ color: '#B12A2A' }}>*</span>{' '}
                <TooltipIcon message={preliminaryInfo} icon={<InfoOutlinedIcon />} placement="right" color="gray" />
              </span>
            ),
            label: 'Requested date of preliminary report',
            name: 'preliminaryDate',
            onlyDate: true,
            placeholder: '12/31/2030',
            required: true,
            type: 'date',
          },
        ]
      : [
          { label: 'Patient Email Address', name: 'email', onChange: handleEmailChange, placeholder: 'Patient Email', required: true, type: 'text' },
          { label: 'Patient Date of Birth', name: 'dateOfBirth', placeholder: '11/23/2020', required: true, type: 'date' },
          { label: 'Patient Preferred Name', name: 'preferredName', placeholder: 'Preferred Name', required: true, type: 'text' },
        ];
  }, [isLawFirm, handleEmailChange]);

  const fieldGroup2 = useMemo(
    () => [
      { label: 'Patient Name', name: 'name', placeholder: 'Patient Name', required: true, type: 'text' },
      { label: 'Patient Telephone', name: 'phoneNumber', placeholder: '+27828501512', required: true, type: 'phone' },
      {
        items: [
          { label: 'Female', value: 'female' },
          { label: 'Male', value: 'male' },
          { label: 'Other', value: 'other' },
        ],
        label: 'Patient Gender',
        name: 'gender',
        placeholder: 'Select Gender',
        required: true,
        type: 'select',
      },
    ],
    []
  );

  const fieldGroup3 = useMemo(() => {
    return isLawFirm
      ? [
          {
            className: 'add-case--patient-question-text-container',
            defaultValue: revelantInfoDefaultText,
            label: 'Relevant Information',
            multiline: true,
            name: 'caseInformation',
            placeholder: 'Case Information',
            rows: 21,
            type: 'text',
            variant: 'outlined',
          },
        ]
      : [
          {
            className: 'add-case--patient-question-text-container',
            label: 'Patient Question',
            multiline: true,
            name: 'questionText',
            placeholder: 'Question?',
            rows: 14,
            type: 'text',
            variant: 'outlined',
          },
        ];
  }, [isLawFirm]);

  const fieldGroup4 = useMemo(() => {
    return [
      {
        customLabel: (
          <span style={{ color: '#172b4d', display: 'flex', gap: '8px' }}>
            Argument you are hoping to make<span style={{ color: '#B12A2A' }}>*</span>{' '}
            <TooltipIcon message={argumentInfo} icon={<InfoOutlinedIcon />} placement="right" color="gray" />
          </span>
        ),
        label: `Argument you are hoping to make`,
        name: 'argument',
        placeholder: 'Argument...',
        required: true,
        style: { margin: '0' },
        type: 'text',
      },
    ];
  }, []);

  const handleUploadedFile = (files, fileType) => {
    if (files) {
      onFilesUpload(files, fileType);
    }
  };

  const isWhiteGlove = useMemo(() => [userDetails?.workspace?.productType].includes(WorkspaceProductType.whiteGlove.value), [userDetails]);
  const workspaceMainFileType = useMemo(() => workspaceDetail?.mainFileType || 'DICOM', [workspaceDetail]);

  const reportDate = moment().add(73, 'hours');
  if (reportDate.day() === 0) reportDate.add(1, 'day');
  else if (reportDate.day() === 6) reportDate.add(2, 'days');
  patient.preferredReportDate = reportDate.toISOString();

  return (
    <Container style={{ padding: '0 1rem' }}>
      <Grid container>
        <Grid item xs={12} style={{ padding: '1rem 1rem 0' }}>
          <div className="mt-2 text-left" style={{ borderBottom: '1px solid #333', paddingBottom: '8px' }}>
            <Typography style={{ color: '#172B4D', fontSize: '20px', fontWeight: 'bold' }}>{isLawFirm ? 'Claimant Information' : 'Patient Information'}</Typography>
          </div>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={4} style={{ padding: '0 1rem' }}>
          <EditFields
            labelClass={classes.label}
            labelPosition="top"
            ref={firstGroupRef}
            data={patient}
            fields={fieldGroup1}
            validate={validations[validationMode]}
            validationErrors={errors}
            showAllErrors={showAllErrors}
          />
        </Grid>
        {!isLawFirm && (
          <Grid item xs={4} style={{ padding: '0 1rem' }}>
            <EditFields
              useAutoFocus={false}
              labelClass={classes.label}
              labelPosition="top"
              ref={secondGroupRef}
              data={patient}
              fields={fieldGroup2}
              validate={validations[mode]}
              validationErrors={errors}
              showAllErrors={showAllErrors}
            />
          </Grid>
        )}

        <Grid item xs={4} style={{ height: '100%', padding: '0 1rem' }} className="mb-0">
          <EditFields
            labelClass={classes.label}
            labelPosition="top"
            ref={thirdGroupRef}
            data={patient}
            fields={fieldGroup3}
            validate={validations[mode]}
            validationErrors={errors}
            showAllErrors={showAllErrors}
          />
        </Grid>

        {isLawFirm && (
          <Grid xs={8} style={{ height: '100%', margin: '0', padding: '0 1rem' }}>
            <EditFields
              useAutoFocus={false}
              labelClass={classes.label}
              labelPosition="top"
              ref={fourthGroupRef}
              data={patient}
              fields={fieldGroup4}
              validate={validations.lawFirm}
              validationErrors={errors}
              showAllErrors={showAllErrors}
            />
          </Grid>
        )}
      </Grid>

      <Grid item xs={12} md={8} style={{ height: '100%', padding: '0 1rem' }}>
        <Typography className="field-label text-left mt-2 mb-1">
          Upload Option <span style={{ color: '#B12A2A' }}>*</span>
        </Typography>

        <Select
          className="outlined-field-padding bold-icon no-background-focus"
          variant="outlined"
          name="selectedOption"
          onChange={handleRadioChange}
          value={status.imageUploadOption || null}
          style={{ display: 'block', overflow: 'hidden' }}>
          {Object.values(isLawFirm ? lawFirmCaseUploadOptions : adminCaseUploadOptions).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid container>
        <Grid item xs={12} style={{ padding: '1rem 1rem 0' }}>
          <div className="mt-1 mb-1 text-left" style={{ borderBottom: '1px solid #333', paddingBottom: '8px' }}>
            <Typography style={{ color: '#172B4D', fontSize: '20px', fontWeight: 'bold' }}>
              {status.imageUploadOption !== allCaseUploadOptions.mailToPOBox.value ? 'Required Materials' : 'P.O. Box mailing address'}
            </Typography>
            <Typography style={{ color: '#172B4D', fontSize: '12px' }}>
              {status.imageUploadOption !== allCaseUploadOptions.mailToPOBox.value
                ? 'The following materials are required to submit a video report request.'
                : 'The following is our P.O. Box address so you can send to Mediphany any disc or folder of the case.'}
            </Typography>
          </div>
        </Grid>
      </Grid>

      {status.imageUploadOption === allCaseUploadOptions.mailToPOBox.value && (
        <address className={classes.poBoxAddress}>
          <div>Mediphany</div>
          <div>13940 Cedar Road</div>
          <div>#374</div>
          <div>University Heights, Ohio 44118-3204</div>
        </address>
      )}

      {status.imageUploadOption === allCaseUploadOptions.imageUpload.value ? (
        <ImageUploadFields
          onUploadedFiles={handleUploadedFile}
          onUploadedReport={setUploadedReport}
          onOpenSelectImageUpload={onOpenSelectImageUploadDialog}
          onOpenUploadReportDialog={onOpenUploadReportDialog}
          status={status}
          imageFileType={workspaceMainFileType}
          onRemoveFiles={onRemoveFiles}
          radiologyReportRequired={workspaceDetail?.radiologyReportRequired}
          mode={mode}
          onRemoveReport={onRemoveReport}
        />
      ) : null}
      {status.imageUploadOption === allCaseUploadOptions.onlinePortal.value ? (
        <OnlinePortalFields status={status} onChangeFormOnlinePortalAccess={handleChangeFormOnlinePortalAccess} />
      ) : null}
      {status.imageUploadOption === allCaseUploadOptions.shareFolderLink.value ? (
        <Grid item xs={12} style={{ padding: '0 3rem' }}>
          <FolderLinkForm status={status} onChangeFormOnlineFolderLink={onChangeOnlineFolderLink} formDescription={false} />
        </Grid>
      ) : null}

      <Grid container style={{ marginTop: '24px' }}>
        <Grid item xs={12} style={{ padding: '0 1rem' }}>
          <div className="mt-1 mb-1 text-left" style={{ borderBottom: '1px solid #333', paddingBottom: '8px' }}>
            <Typography style={{ color: '#172B4D', fontSize: '20px', fontWeight: 'bold' }}>Optional Materials</Typography>
            <Typography style={{ color: '#172B4D', fontSize: '12px' }}>
              The following materials are optional and can be added in order to assist us with producing the video report.
            </Typography>
          </div>
        </Grid>
      </Grid>

      <ImageUploadOptionalFields
        onRemoveAdditionalFiles={handleRemoveAdditionalFiles}
        onOpenAdditionalFilesDialog={handleOpenAdditionalFilesDialog}
        additionalFiles={additionalFiles}
      />

      <Grid container>
        <Grid item xs={12} style={{ padding: '1rem 1rem 0' }}>
          <div className="mt-1 mb-1 text-left" style={{ borderBottom: '1px solid #333', paddingBottom: '8px' }}>
            <Typography style={{ color: '#172B4D', fontSize: '20px', fontWeight: 'bold' }}>Notification Settings</Typography>
            <Typography style={{ color: '#172B4D', fontSize: '12px' }}>Choose the notification settings you would like for this video report.</Typography>
          </div>
        </Grid>
      </Grid>

      <NotificationSettings
        admins={admins}
        selectedAdmins={selectedAdmins}
        onUpdateSelectedAdmins={handleUpdateSelectedAdmins}
        notifyMe={notifyMe}
        onUpdateNotifyMe={handleUpdateNotifyMe}
      />
      {isWhiteGlove ? (
        <>
          <Grid container>
            <Grid item xs={12} style={{ padding: '0 1rem' }}>
              <div className="mt-1 mb-1 text-left" style={{ borderBottom: '1px solid #333', paddingBottom: '8px' }}>
                <Typography style={{ color: '#172B4D', fontSize: '20px', fontWeight: 'bold' }}>Payment Method</Typography>
                <Typography style={{ color: '#172B4D', fontSize: '12px' }}>Choose how to handle the payment for this video report.</Typography>
              </div>
            </Grid>
          </Grid>

          <PaymentMethod
            userDetails={userDetails}
            onUpdatePaymentMethod={handleUpdatePaymentMethod}
            onUpdateSelectedPackage={handleUpdateSelectedPackage}
            selectedPackage={selectedPackage}
            selectedPaymentMethod={selectedPaymentMethod}
            discountData={discountData}
          />
        </>
      ) : null}
      {!isWhiteGlove && !isLawFirm ? (
        <>
          <Grid container>
            <Grid item xs={12} style={{ padding: '0 1rem' }}>
              <div className="mt-1 mb-1 text-left" style={{ borderBottom: '1px solid #333', paddingBottom: '8px' }}>
                <Typography style={{ color: '#172B4D', fontSize: '20px', fontWeight: 'bold' }}>Timing Options</Typography>
                <Typography style={{ color: '#172B4D', fontSize: '12px' }}>Choose the solution that’s right for your patient! </Typography>
              </div>
            </Grid>
          </Grid>

          <TimingOptions userDetails={userDetails} onUpdateSelectedPackage={handleUpdateSelectedPackage} selectedPackage={selectedPackage} discountData={discountData} />
        </>
      ) : null}
      <Grid container>
        <Grid item xs={12} md={8} style={{ padding: '0 1rem' }}>
          <div className="mt-3 text-left">
            <Typography style={{ color: '#172B4D', fontSize: '20px', fontWeight: 'bold' }}>Confirm Order</Typography>
            {!isLawFirm && (
              <Typography style={{ color: '#172B4D', fontSize: '12px' }}>
                Once you complete the order request below an email with a payment link will be sent through to your patient. Once paid our team will begin working on the video
                report.
              </Typography>
            )}
          </div>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} style={{ padding: '0 1rem' }}>
          <div className={`${classes.checkboxContainer} text-left`}>
            <FormControlLabel
              classes={{ root: 'mediphany-formcontrol-root' }}
              control={
                <Checkbox
                  classes={{ root: 'mediphany-checkbox-root' }}
                  value={termsCheck}
                  onChange={(e) => handleTermsCheckChange('terms', e)}
                  name="checkedB"
                  size="small"
                  color="primary"
                />
              }
              label={
                <Typography className={`${classes.typographyLabel} text-left`}>
                  I have read, understand and agree to Mediphany’s&nbsp;
                  <a href="https://mediphany.com/terms-and-conditions/" className={classes.link} target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>
                  &nbsp;and&nbsp;
                  <a href="https://mediphany.com/privacy-policy/" className={classes.link} target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                  .
                </Typography>
              }
            />
          </div>
        </Grid>
      </Grid>
      <Grid container justify="space-between" className="mt-0 mb-3" style={{ padding: '0 1rem' }}>
        <Grid item className="mt-1">
          <LoadingButton
            variant="contained"
            onClick={onSubmitClick}
            disabled={!termsCheck || !onButtonEnabled || !areRequiredFieldsFilled}
            style={{ marginRight: '16px' }}
            color="primary"
            loading$={isSubmitting$}>
            Confirm Request
          </LoadingButton>
        </Grid>

        <Grid item className="mt-1">
          <Button variant="outlined" color="primary" onClick={onCancelClick}>
            Cancel - Back to Dashboard
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};
